/* eslint-disable ember/no-runloop */
import { action } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { ComparisonSet } from 'frontend-utils/utils/constants/benchmark-insight';
import type * as Highcharts from 'highcharts';
import SkeletonBreakdownChartIllustration from 'lumen/assets/images/benchmark-insight/skeleton/skeleton-breakdown-chart.svg?raw';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import type { OccupancyView } from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-occupancy-view';
import HotelBenchmarkInsightSkeleton from 'lumen/components/hotel/benchmark-insight/skeleton';
import type { BeniTooltipData } from 'lumen/components/hotel/benchmark-insight/tooltips/beni-tooltip';
import HotelBenchmarkInsightTooltipsBeniTooltip from 'lumen/components/hotel/benchmark-insight/tooltips/beni-tooltip';
import type { BeniBreakdown, BeniMetaComparisonSet } from 'lumen/models';
import getElement from 'lumen/modifiers/get-element';
import {
  type BreakdownsDict,
  buildBreakdownDict,
  type CombinedBreakdowns,
} from 'lumen/utils/benchmark-insight/chart';
import {
  BENI_COMPARISON_SET_LONG_TRANSLATIONS,
  BREAKDOWN_CHART_COLUMN_SERIES_GROUP_PADDING,
  BREAKDOWN_CHART_COLUMN_SERIES_POINT_PADDING,
  BREAKDOWN_CHART_HEIGHT,
  CHANNEL_TRANSLATIONS,
  type ChannelHierarchyItem,
  isChannelHierarchyItem,
  type Kpi,
  KPI_LONG_TRANSLATIONS,
  KPIS,
  type ReferencePeriod,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { DateRangeType } from 'lumen/utils/constants/benchmark-insight/beni-table-constant-values';
import type { ChartCustomPoint } from 'lumen/utils/constants/highcharts-constants';
import type MappingCategoryBenchmark from 'lumen/utils/datamodels/mapping-category-benchmark';
import calculateRemainderFromSum from 'lumen/utils/numeric/get-remainder';

import styles from './channel-chart.module.css';

const SERIES_OCCUPANCY_MIX_OTHER_ID = 'occupancyMixOther' as const;
type SeriesOccupancyMixOtherId = typeof SERIES_OCCUPANCY_MIX_OTHER_ID;

export type ChannelChartSeriesData = (number | null)[];

type MouseCursorOptions =
  | {
      cursor?: never;
      enableMouseTracking: boolean;
    }
  | {
      cursor: 'pointer' | 'default';
      enableMouseTracking?: never;
    };

export type ChannelChartSeries = {
  color?: string;
  data: ChannelChartSeriesData;
  id: ChannelHierarchyItem | SeriesOccupancyMixOtherId;
  name: string;
} & MouseCursorOptions;

interface HotelBenchmarkInsightChartsChannelChartSignature {
  Args: {
    channel: ChannelHierarchyItem | null;
    isFiltering: boolean;
    onUpdateChartReference?: (chart: Highcharts.Chart) => void;
    ownComparisonSetBreakdowns: BeniBreakdown[];
    ownComparisonSetMeta: BeniMetaComparisonSet | null;
    pickupTranslation: string;
    primaryComparisonSetBreakdowns: BeniBreakdown[];
    primaryComparisonSetMeta: BeniMetaComparisonSet | null;
    referencePeriod: ReferencePeriod;
    rootCategory: MappingCategoryBenchmark | null;
    secondaryComparisonSetBreakdowns: BeniBreakdown[];
    secondaryComparisonSetMeta: BeniMetaComparisonSet | null;
    selectedKpiChannel: Kpi;
    selectedOccupancyView: OccupancyView;
    selectedRange: DateRangeType;
    setChannel: (channel: ChannelHierarchyItem | null) => void;
    showMix: boolean;
    showSkeleton: boolean;
    showSkeletonAnimation: boolean;
    visibleKpis: Kpi[];
  };
}

export default class HotelBenchmarkInsightChartsChannelChart extends Component<HotelBenchmarkInsightChartsChannelChartSignature> {
  @service('benchmark-insight/beni-chart-los-service')
  declare benchmarkInsightBeniChartLosService: Services['benchmark-insight/beni-chart-los-service'];
  // eslint-disable-next-line ember/no-unused-services
  @service('benchmark-insight/beni-chart-service')
  declare benchmarkInsightBeniChartService: Services['benchmark-insight/beni-chart-service'];
  @service('benchmark-insight/beni-service')
  declare beniService: Services['benchmark-insight/beni-service'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @tracked tooltipData: BeniTooltipData | null = null;
  @tracked tooltipElement: HTMLElement | null = null;

  TICK_AMOUNT_DEFAULT = 4;
  TICK_AMOUNT_MIX_VIEW = 6;

  get categories(): MappingCategoryBenchmark[] {
    const { rootCategory } = this.args;

    if (!rootCategory) {
      return [];
    }

    const categories: MappingCategoryBenchmark[] = [];

    if (rootCategory.children.length) {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      const traverseCategories = (category: MappingCategoryBenchmark) => {
        if (rootCategory.children?.includes(category)) {
          categories.push(category);
        } else if (category.children) {
          category.children.forEach(traverseCategories);
        }
      };
      traverseCategories(rootCategory);
    } else {
      // User is filtering on a leaf category, show the category itself instead of the children.
      categories.push(rootCategory);
    }

    return categories;
  }

  get channelDict(): BreakdownsDict {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
      ownComparisonSetBreakdowns,
      primaryComparisonSetBreakdowns,
      secondaryComparisonSetBreakdowns,
    } = this.args;

    return buildBreakdownDict({
      ownComparisonSetBreakdowns,
      ownComparisonSetMeta,
      primaryComparisonSetBreakdowns,
      primaryComparisonSetMeta,
      secondaryComparisonSetBreakdowns,
      secondaryComparisonSetMeta,
    });
  }

  get chartCategories(): string[] {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    } = this.args;

    const comparisonSetTranslations: string[] = [];

    const pushComparisonSetTranslation = (
      comparisonSetMeta: BeniMetaComparisonSet | null,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ) => {
      if (comparisonSetMeta && comparisonSetMeta.isVisible) {
        const { selectedRange, selectedKpiChannel } = this.args;

        const isHiddenFutureCompsetData =
          this.beniService.isHiddenFutureCompsetData(
            comparisonSetMeta.comparisonSetType,
            selectedRange.start,
            selectedKpiChannel,
          );

        if (isHiddenFutureCompsetData) {
          return;
        }

        comparisonSetTranslations.push(
          this.intl.t(
            BENI_COMPARISON_SET_LONG_TRANSLATIONS[
              comparisonSetMeta.comparisonSetType
            ],
          ),
        );
      }
    };

    [
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    ].forEach(pushComparisonSetTranslation);

    return comparisonSetTranslations;
  }

  get chartData(): ChannelChartSeries[] {
    const {
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
      channel,
      rootCategory,
    } = this.args;

    const ownComparisonSetExists = ownComparisonSetMeta?.isVisible;
    const primaryComparisonSetExists = primaryComparisonSetMeta?.isVisible;
    const secondaryComparisonSetExists = secondaryComparisonSetMeta?.isVisible;

    const channelSeries: ChannelChartSeries[] = [];

    this.categories.forEach((category) => {
      const breakdownsByCategory: CombinedBreakdowns | null =
        this.channelDict[category.id] ?? null;

      if (!breakdownsByCategory) {
        return;
      }

      const finalSeriesData: (number | null)[] = [];

      if (ownComparisonSetExists) {
        finalSeriesData.push(
          ...this.getSeriesData(
            ownComparisonSetMeta.comparisonSetType,
            breakdownsByCategory.ownComparisonSetBreakdown,
          ),
        );
      }

      if (primaryComparisonSetExists) {
        finalSeriesData.push(
          ...this.getSeriesData(
            primaryComparisonSetMeta.comparisonSetType,
            breakdownsByCategory.primaryComparisonSetBreakdown,
          ),
        );
      }

      if (secondaryComparisonSetExists) {
        finalSeriesData.push(
          ...this.getSeriesData(
            secondaryComparisonSetMeta.comparisonSetType,
            breakdownsByCategory.secondaryComparisonSetBreakdown,
          ),
        );
      }

      const { id } = category;

      if (!isChannelHierarchyItem(id)) {
        return;
      }

      const isAlreadyFiltering = category.id === channel;

      const series: ChannelChartSeries = {
        id,
        name: category.getDisplayName(this.intl, this.errorTracking) ?? '',
        data: finalSeriesData,
        cursor: isAlreadyFiltering ? 'default' : 'pointer',
      };

      channelSeries.push(series);
    });

    // Show a remainder series to fill up to 100% in mix view. The top categories should already add up to 100%, so only show the remainder if a channel is selected.
    // Don't show the remainder series if the user is filtering on a leaf category.
    if (this.args.showMix && rootCategory?.children.length) {
      const dataSets = channelSeries.map((series) => {
        return { data: series.data };
      });
      const orderedData = [...this.sumDataArrays(dataSets)].map((item) => {
        return item.data;
      });

      const seriesOccupancyMixOther = {
        id: SERIES_OCCUPANCY_MIX_OTHER_ID,
        name: this.intl.t('general.other'),
        showInLegend: false,
        color: 'var(--prism-color-general-neutral-600)',
        data: orderedData,
        // We can't calculate tooltip data for Others series, because we can't simply sum up ADR & RevPAR in the frontend.
        // So we disable mouse hover and tooltip display for this series.
        enableMouseTracking: false,
      };

      channelSeries.push(seriesOccupancyMixOther);
    }

    return channelSeries;
  }

  get chartOptions(): Highcharts.Options {
    const { showMix } = this.args;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    const stacking = showMix ? 'normal' : undefined;
    const pointPadding = showMix
      ? 0.09
      : BREAKDOWN_CHART_COLUMN_SERIES_POINT_PADDING;
    const groupPadding = showMix
      ? 0
      : BREAKDOWN_CHART_COLUMN_SERIES_GROUP_PADDING;

    return {
      chart: {
        type: 'column',
        zoomType: undefined,
        height: BREAKDOWN_CHART_HEIGHT,
        spacing: [10, 0, 0, 0],
        reflow: true,
      },
      xAxis: this.xAxis,
      yAxis: this.yAxis,
      plotOptions: {
        column: {
          stacking,
          pointPadding,
          groupPadding,
          pointWidth: undefined,
          point: {
            events: {
              // Highlight the hovered data in each series
              mouseOver(): void {
                this.series.chart.series.forEach((series) => {
                  if (series && series.points && series !== this.series) {
                    series.points.forEach((point: ChartCustomPoint) => {
                      if (point.graphic) {
                        point.graphic.element.style.transition = 'opacity 0.3s';
                        point.graphic.attr({
                          opacity: 0.3,
                        });
                      }
                    });
                  }
                }, this);
              },
              mouseOut(): void {
                this.series.chart.series.forEach((series) => {
                  if (series && series.points && series !== this.series) {
                    series.points.forEach((point: ChartCustomPoint) => {
                      if (point.graphic) {
                        point.graphic.attr({
                          opacity: 1,
                        });
                      }
                    });
                  }
                });
              },
            },
          },
        },
        series: {
          animation: true,
          point: {
            events: {
              click(): void {
                const { id } = this.series.userOptions;
                if (!id) {
                  return;
                }

                if (!isChannelHierarchyItem(id) || that.args.channel === id) {
                  return;
                }

                this.series.chart.tooltip.hide();
                that.args.setChannel(id);
              },
            },
          },
        },
      },
      tooltip: {
        padding: 0,
        borderWidth: 0,
        shadow: false,
        distance: 20,
        outside: true,
        shared: false,
        useHTML: true,
        formatter(
          this: Highcharts.TooltipFormatterContextObject,
        ): string | null {
          if (!that.tooltipElement) {
            return null;
          }

          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();
          const channel = this.series.userOptions.id;

          if (
            !channel ||
            typeof channel !== 'string' ||
            !isChannelHierarchyItem(channel)
          ) {
            return null;
          }

          const combinedBreakdowns = that.channelDict[channel];
          if (!combinedBreakdowns) {
            return null;
          }

          const tooltipData = {
            ...combinedBreakdowns,
            prefix: that.intl.t(CHANNEL_TRANSLATIONS[channel]),
          };
          that.tooltipData = tooltipData;
          end();

          return that.tooltipElement.innerHTML;
        },
      },
    };
  }

  get maxXAxisTicIndex(): number {
    const comparisonSetMetas = [
      this.args.ownComparisonSetMeta,
      this.args.primaryComparisonSetMeta,
      this.args.secondaryComparisonSetMeta,
    ];

    return comparisonSetMetas.reduce((count, meta) => {
      const isAvailable = meta?.comparisonSetType && meta.isVisible;
      return isAvailable ? count + 1 : count;
    }, 0);
  }

  get xAxis(): Highcharts.XAxisOptions {
    const { showMix } = this.args;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const categories = this.chartCategories;
    const min = showMix ? -1 : undefined;
    const max = showMix ? this.maxXAxisTicIndex : undefined;

    return {
      categories,
      lineWidth: 0,
      tickmarkPlacement: 'on',
      tickWidth: 1,
      minPadding: 0,
      startOnTick: true,

      // Add an extra tick to the left and right of the stacked columns
      // this is the best way to center the columns in the chart
      max,
      min,

      // hide the first and last labels in mix view
      showFirstLabel: !showMix,
      showLastLabel: !showMix,

      // hide the first and last ticks in mix view
      tickPositioner(): Highcharts.AxisTickPositionsArray {
        const categoryIndexies = this.categories.map((x, index) => index);

        if (!that.args.showMix) {
          return categoryIndexies;
        }

        const maxCatIndex = Math.max(...categoryIndexies);
        const maxPlusOne = maxCatIndex + 1;
        const result = [-1, ...categoryIndexies, maxPlusOne];

        let allTicks: Highcharts.Dictionary<Highcharts.Tick> = {};
        if (this.chart.xAxis[0]?.ticks) {
          allTicks = this.chart.xAxis[0]?.ticks;
        }

        const keys = Object.keys(allTicks).map(Number);
        const maxKey = Math.max(...keys);

        const firstTick = this.chart.xAxis[0]?.ticks[-1];
        const lastTick = this.chart.xAxis[0]?.ticks[maxKey];

        firstTick?.mark?.hide();
        lastTick?.mark?.hide();

        return result;
      },
    };
  }

  get yAxis(): Highcharts.YAxisOptions {
    const { selectedKpiChannel, showMix } = this.args;

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const max = showMix ? 1 : null;
    const tickAmount = showMix
      ? this.TICK_AMOUNT_MIX_VIEW
      : this.TICK_AMOUNT_DEFAULT;

    return {
      // have the largest blocks at the bottom
      reversedStacks: false,
      max,
      min: 0,
      title: {
        margin: 15,
        text: this.intl.t(KPI_LONG_TRANSLATIONS[selectedKpiChannel]),
      },
      labels: {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          if (typeof this.value !== 'number') {
            return this.value;
          }

          return that.benchmarkInsightBeniChartService.yAxisLabelFormatter(
            this.value,
            selectedKpiChannel === KPIS.OCCUPANCY,
          );
        },
      },
      tickAmount,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getSeriesData(
    comparisonSetType: ComparisonSet,
    breakdown: BeniBreakdown | null,
  ) {
    const { selectedKpiChannel } = this.args;

    const result: (number | null)[] = [];

    if (!comparisonSetType || !breakdown) {
      return result;
    }

    const { selectedRange } = this.args;

    const isHiddenFutureCompsetData =
      this.beniService.isHiddenFutureCompsetData(
        comparisonSetType,
        selectedRange.start,
        selectedKpiChannel,
      );

    if (isHiddenFutureCompsetData) {
      return result;
    }

    const correctValue = this.args.showMix
      ? KPIS.OCCUPANCY_MIX
      : selectedKpiChannel;

    const value = breakdown.current.total[correctValue];

    result.push(value);

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  mapCategoryToSeries(
    category: MappingCategoryBenchmark,
    color: string | undefined,
  ) {
    const {
      selectedKpiChannel,
      ownComparisonSetMeta,
      primaryComparisonSetMeta,
      secondaryComparisonSetMeta,
    } = this.args;

    const { name } = category;

    if (!name) {
      return;
    }

    const breakdownsByComparisonSet = this.channelDict[name];

    const data: (number | null)[] = [];

    if (breakdownsByComparisonSet) {
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      const pushComparisonSetValueToData = (key: keyof CombinedBreakdowns) => {
        const comparisonSetBreakdown = breakdownsByComparisonSet[key];

        if (!comparisonSetBreakdown) {
          return;
        }

        data.push(comparisonSetBreakdown.current.total[selectedKpiChannel]);
      };

      if (ownComparisonSetMeta) {
        pushComparisonSetValueToData('ownComparisonSetBreakdown');
      }

      if (primaryComparisonSetMeta) {
        pushComparisonSetValueToData('primaryComparisonSetBreakdown');
      }

      if (secondaryComparisonSetMeta) {
        pushComparisonSetValueToData('secondaryComparisonSetBreakdown');
      }
    }

    const series: Highcharts.SeriesColumnOptions = {
      name: isChannelHierarchyItem(name)
        ? this.intl.t(CHANNEL_TRANSLATIONS[name])
        : name,
      id: name,
      type: 'column',
      data,
      color,
      showInLegend: false,
    };

    return series;
  }

  sumDataArrays(
    seriesArray: { data: ChannelChartSeriesData }[],
  ): { data: number | null; id: number }[] {
    const groupedBySeries =
      this.benchmarkInsightBeniChartLosService.groupDataBySeries(seriesArray);
    const remaindersSeriesData = groupedBySeries.map((group, index) => {
      const remainder = calculateRemainderFromSum(group.values, 1);
      return { id: index, data: remainder };
    });

    return remaindersSeriesData;
  }

  @action
  setTooltipElement(element: HTMLElement): void {
    this.tooltipElement = element;
  }

  <template>
    {{#if @showSkeleton}}
      <div class={{styles.skeleton}}>
        <HotelBenchmarkInsightSkeleton
          @imageHtml={{SkeletonBreakdownChartIllustration}}
          @showAnimation={{@showSkeletonAnimation}}
        />
      </div>
    {{else}}
      <GeneralChartsOtaChart
        @chartOptions={{this.chartOptions}}
        @model={{this.chartData}}
        @onUpdateChartReference={{@onUpdateChartReference}}
      />
    {{/if}}

    <div hidden {{getElement this.setTooltipElement}}>
      {{#if this.tooltipData}}
        <HotelBenchmarkInsightTooltipsBeniTooltip
          @ownComparisonSetMeta={{@ownComparisonSetMeta}}
          @pickupTranslation={{@pickupTranslation}}
          @primaryComparisonSetMeta={{@primaryComparisonSetMeta}}
          @referencePeriod={{@referencePeriod}}
          @secondaryComparisonSetMeta={{@secondaryComparisonSetMeta}}
          @selectedKpi={{@selectedKpiChannel}}
          @selectedRange={{@selectedRange}}
          @showMix={{false}}
          @tooltipData={{this.tooltipData}}
          @visibleKpis={{@visibleKpis}}
        />
      {{/if}}
    </div>
  </template>
}
